import {Component, OnInit} from '@angular/core';
import {Observable} from 'rxjs';
import {WaihonaUserRef} from '../../../domain/user/WaihonaUserRef';
import {SubscriptionCleaner} from '../../../util/SubscriptionCleaner';
import {WaihonaUserService} from '../../../services/WaihonaUserService';
import {NGXLogger} from 'ngx-logger';
import {BadgeService} from '../../../services/BadgeService';
import {BadgeRef} from '../../../domain/badges/BadgeRef';
import {LocalizationService} from '../../../services/LocalizationService';
import {RoleService} from '../../../services/common/RoleService';
import {PermissionType} from '../../../domain/user/Permission';

@Component({selector:'app-badge-admin', templateUrl:'./badge-admin.component.html', styleUrls:['./badge-admin.component.scss']})
export class BadgeAdminComponent extends SubscriptionCleaner implements OnInit {
    public waihonaUserRefs$:Observable<WaihonaUserRef[]>;
    public badgeRefs$:Observable<BadgeRef[]>;
    public selectedBadge:BadgeRef;
    public awardLevel:number = 1;
    public usersToReceiveBadge:WaihonaUserRef[] = [];
    public badgeAdmins:any[] = [];
    public usersToGrant:WaihonaUserRef[] = [];
    public isAwarding = false;
    public isManaging = false;
    public isLoadingAdmins = false;
    public awardError = '';
    public awardSuccess = '';
    public adminError = '';
    public adminSuccess = '';
    public localize = {badgeName:(ref:BadgeRef) => this.localizationService.LocalizeTools.ref(ref.localization, this.localizationService.language, 'name')};
    constructor(private badgeService:BadgeService, private waihonaUserService:WaihonaUserService,
                private localizationService:LocalizationService, protected logger:NGXLogger, private roleService:RoleService) {
        super();
        this.badgeRefs$ = badgeService.repo.watchListAsRef$();
        this.waihonaUserRefs$ = waihonaUserService.repo.watchListAsRef$();
    }
    public get canAward():boolean { return this.roleService.hasPermissionFor(PermissionType.award_badge); }
    public get canManage():boolean { return this.roleService.hasPermissionFor(PermissionType.manage_badge_admins); }
    ngOnInit():void { if (this.canManage) { this.loadAdmins(); } }
    public loadAdmins():void {
        if (!this.canManage) { return; }
        this.isLoadingAdmins = true;
        this.trackSubscription(this.waihonaUserService.repo.list$(false).take(1).subscribe({
            next: users => {
                this.badgeAdmins = users.filter(user => (user.roles || []).some(role =>
                    role.type === 'badgeAdmin' || (role.type === 'badgeEditor' && role.organization == null)));
                this.isLoadingAdmins = false;
            },
            error: error => { this.logger.error(error); this.adminError = 'Could not load badge administrators. Please retry.'; this.isLoadingAdmins = false; }
        }));
    }
    public async onConfirmAwardClick():Promise<void> {
        if (!this.canAward || this.isAwarding) { return; }
        this.awardError = ''; this.awardSuccess = '';
        if (!this.selectedBadge || !this.usersToReceiveBadge?.length || this.usersToReceiveBadge.length > 50
            || !Number.isSafeInteger(this.awardLevel) || this.awardLevel < 1) {
            this.awardError = 'Choose a badge, 1–50 recipients, and a positive whole-number level.'; return;
        }
        this.isAwarding = true;
        try {
            const result = await this.badgeService.awardBadges$(this.selectedBadge.guid,
                this.usersToReceiveBadge.map(user => user.guid), this.awardLevel).take(1).toPromise();
            this.awardSuccess = `Awarded to ${result.awarded} user(s); ${result.alreadyAwarded} already had this badge and level.`;
            this.resetAward();
        } catch (error) { this.logger.error(error); this.awardError = 'Badges could not be awarded. Your selections are retained; please retry.'; }
        finally { this.isAwarding = false; }
    }
    public resetAward():void { this.selectedBadge = null; this.usersToReceiveBadge = []; this.awardLevel = 1; }
    public async setAdmins(users:WaihonaUserRef[], enabled:boolean):Promise<void> {
        if (!this.canManage || this.isManaging || !users?.length) { return; }
        this.isManaging = true; this.adminError = ''; this.adminSuccess = '';
        try {
            const result = await this.badgeService.setAdministrators$(users.map(user => user.guid), enabled).take(1).toPromise();
            this.adminSuccess = `${enabled ? 'Granted' : 'Revoked'} badge administrator access for ${result.updated} user(s). Affected users should sign out and back in.`;
            if (enabled) { this.usersToGrant = []; }
            this.loadAdmins();
        } catch (error) { this.logger.error(error); this.adminError = 'Could not update badge administrators. Please retry.'; }
        finally { this.isManaging = false; }
    }
}
